import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Line,
  ReferenceLine,
} from 'recharts'
import type { EquityPoint } from './types'
import { COLORS, FONT_FAMILY } from './theme'

interface EquityChartProps {
  data: EquityPoint[]
}

function fmtCurrency(value: number | string | readonly (number | string)[] | undefined): string {
  if (typeof value === 'number') return `$${value.toFixed(2)}`
  if (Array.isArray(value)) return `$${Number(value[0]).toFixed(2)}`
  return '—'
}

export function EquityChart({ data }: EquityChartProps) {
  if (!data.length) return null

  const initialCapital = data[0]?.initialCapital ?? 10000

  return (
    <div className="w-full h-full flex flex-col">
      <div className="mb-2">
        <h3 className="font-bold text-[14px]" style={{ color: COLORS.text, fontFamily: FONT_FAMILY }}>
          Curvas de Equity - 5000 Simulaciones
        </h3>
      </div>
      <div className="flex-1 min-h-0 min-h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
            <defs>
              <linearGradient id="band5_95" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={COLORS.primary} stopOpacity={0.08} />
                <stop offset="100%" stopColor={COLORS.primary} stopOpacity={0.08} />
              </linearGradient>
              <linearGradient id="band10_90" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={COLORS.primary} stopOpacity={0.15} />
                <stop offset="100%" stopColor={COLORS.primary} stopOpacity={0.15} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
            <XAxis
              dataKey="tradeIndex"
              tick={{ fill: COLORS.textSecondary, fontSize: 10, fontFamily: FONT_FAMILY }}
              axisLine={{ stroke: COLORS.border }}
              tickLine={{ stroke: COLORS.border }}
              label={{ value: 'Número de Trade', position: 'insideBottom', offset: -5, fill: COLORS.textSecondary, fontSize: 10 }}
            />
            <YAxis
              tick={{ fill: COLORS.textSecondary, fontSize: 10, fontFamily: FONT_FAMILY }}
              axisLine={{ stroke: COLORS.border }}
              tickLine={{ stroke: COLORS.border }}
              tickFormatter={fmtCurrency}
              domain={['auto', 'auto']}
              label={{ value: 'Equity ($)', angle: -90, position: 'insideLeft', fill: COLORS.textSecondary, fontSize: 10 }}
            />
            <Tooltip
              contentStyle={{ backgroundColor: COLORS.panel, border: `1px solid ${COLORS.border}`, borderRadius: '0px' }}
              itemStyle={{ fontSize: 11, fontFamily: FONT_FAMILY }}
              formatter={(value) => fmtCurrency(value)}
              labelStyle={{ color: COLORS.textSecondary, fontSize: 10 }}
            />

            <Area type="monotone" dataKey="p5" stroke="none" fill="url(#band5_95)" />
            <Area type="monotone" dataKey="p10" stroke="none" fill="url(#band10_90)" />

            <Line type="monotone" dataKey="median" stroke={COLORS.primary} strokeWidth={2.5} dot={false} name="Mediana" />
            <Line type="monotone" dataKey="p5" stroke={COLORS.secondary} strokeWidth={1.5} dot={false} strokeDasharray="5 5" name="5th pct" />
            <Line type="monotone" dataKey="p95" stroke={COLORS.success} strokeWidth={1.5} dot={false} strokeDasharray="5 5" name="95th pct" />
            <ReferenceLine y={initialCapital} stroke={COLORS.initialCapital} strokeDasharray="3 3" strokeOpacity={0.5} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
